import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ModalProvider } from './context/ModalContext';
import { BrandingProvider } from './context/BrandingContext';
import PatientFormPage from './pages/PatientFormPage';
import LoginPage from './pages/LoginPage';
import AdminPortalPage from './pages/AdminPortalPage';
import LinksPage from './pages/LinksPage';
import SubmissionsPage from './pages/SubmissionsPage';
import ManageUsersPage from './pages/ManageUsersPage';
import ManageClinicsPage from './pages/ManageClinicsPage';
import FieldEditorPage from './pages/FieldEditorPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import MandatoryTwoFactorSetupPage from './pages/MandatoryTwoFactorSetupPage';
import ClinicTemplatesPage from './pages/ClinicTemplatesPage';
import ClinicLinksPage from './pages/ClinicLinksPage';
import ClinicSubmissionsPage from './pages/ClinicSubmissionsPage';
import TrashPage from './pages/TrashPage';
import CompaniesPage from './pages/CompaniesPage';
import CompanyActivityPage from './pages/CompanyActivityPage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import MaintenancePage from './pages/MaintenancePage';
import NotFoundPage from './pages/NotFoundPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminLayout from './components/layout/AdminLayout';
import SubmissionsAccessGate from './components/layout/SubmissionsAccessGate';

function App() {
  return (
    // Router is outermost so BrandingProvider can read the route: a fill link's
    // token in the path is what resolves which company's brand to show before
    // any session exists.
    <BrowserRouter>
    <AuthProvider>
      <BrandingProvider>
      <ModalProvider>
        <Routes>
          {/* Public routes - NO layout */}
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/forms" element={<Navigate to="/admin/templates" replace />} />
          <Route path="/form/:formId" element={<PatientFormPage />} />
          <Route path="/fill/:token" element={<PatientFormPage />} />
          <Route path="/maintenance" element={<MaintenancePage />} />
          
          {/* Mandatory 2FA setup route - requires auth but not admin access, no layout */}
          <Route
            path="/setup-2fa"
            element={
              <ProtectedRoute requireAdmin={false}>
                <MandatoryTwoFactorSetupPage />
              </ProtectedRoute>
            }
          />
          
          {/* Field Editor - standalone full-screen page (outside AdminLayout) */}
          <Route
            path="/admin/editor/:formId"
            element={
              <ProtectedRoute requireAdmin={true} companyAdminOnly={true}>
                <FieldEditorPage />
              </ProtectedRoute>
            }
          />

          {/* Admin routes - with portal layout (platform owner or company admin).
              Children render through AdminLayout's <Outlet />, so they must be
              nested here rather than passed as children. */}
          <Route path="/admin" element={
            <ProtectedRoute requireAdmin={true}><AdminLayout /></ProtectedRoute>
          }>
            <Route path="dashboard" element={<ProtectedRoute companyAdminOnly><AdminPortalPage /></ProtectedRoute>} />
            <Route path="templates" element={<ProtectedRoute companyAdminOnly><AdminPortalPage /></ProtectedRoute>} />
            <Route path="links" element={<ProtectedRoute companyAdminOnly><LinksPage /></ProtectedRoute>} />
            <Route path="submissions" element={<ProtectedRoute companyAdminOnly><SubmissionsPage /></ProtectedRoute>} />
            <Route path="users" element={<ManageUsersPage />} />
            <Route path="clinics" element={<ManageClinicsPage />} />
            <Route path="company" element={<CompanySettingsPage />} />
            <Route path="trash" element={<ProtectedRoute companyAdminOnly><TrashPage /></ProtectedRoute>} />
            {/* Companies span every tenant, so this one route is owner-only */}
            <Route path="companies" element={
              <ProtectedRoute requireSuperadmin={true}><CompaniesPage /></ProtectedRoute>
            } />
            <Route path="companies/:companyId" element={
              <ProtectedRoute requireSuperadmin={true}><CompanyActivityPage /></ProtectedRoute>
            } />
            <Route path="security" element={<ChangePasswordPage />} />
            <Route path="change-password" element={<ChangePasswordPage />} />
            <Route path="maintenance" element={<ChangePasswordPage />} />
          </Route>

          {/* Clinic user routes - with portal layout */}
          {/* requireClinic: superadmin users are redirected to /admin/* equivalent */}
          <Route path="/portal" element={
            <ProtectedRoute requireAdmin={false} requireClinic={true}><AdminLayout /></ProtectedRoute>
          }>
            <Route index element={<Navigate to="templates" replace />} />
            <Route path="templates" element={<ClinicTemplatesPage />} />
            <Route path="links" element={<ClinicLinksPage />} />
            <Route path="submissions" element={
              <SubmissionsAccessGate><ClinicSubmissionsPage /></SubmissionsAccessGate>
            } />
          </Route>

          {/* Legacy redirects */}
          <Route path="/links" element={<Navigate to="/admin/links" replace />} />
          <Route path="/submissions" element={<Navigate to="/admin/submissions" replace />} />
          
          {/* Missing tokens and unknown paths are not staff login entry points. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ModalProvider>
      </BrandingProvider>
    </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
